import { useQuery, gql } from '@apollo/client';
import { COMPANY, TASK } from 'customer-data-objects/constants/ObjectTypes';
import { LINKED_IN_CONNECT, LINKED_IN_MESSAGE } from 'customer-data-objects/engagement/TaskTypes';
import PropertyOptionRecord from 'customer-data-objects/property/PropertyOptionRecord';
import PropertyRecord from 'customer-data-objects/property/PropertyRecord';
import { ENUMERATION } from 'customer-data-objects/property/PropertyTypes';
import InboundDbImportProperty from 'customer-data-objects/property/special/InboundDbImportProperty';
import { ENGAGEMENT_SOURCE, ENGAGEMENT_SOURCE_ID, REPEAT_STATUS, STATUS, TYPE } from 'customer-data-objects/task/TaskPropertyNames';
import I18n from 'I18n';
import { List } from 'immutable';
import { useEffect, useMemo, useState } from 'react';
import filterNot from 'transmute/filterNot';
import get from 'transmute/get';
import getIn from 'transmute/getIn';
import isEmpty from 'transmute/isEmpty';
import memoize from 'transmute/memoize';
import partial from 'transmute/partial';
import { DEFERRED, IN_PROGRESS, WAITING } from '../../legacy/legacyBoard/crm_ui/constants/engagementStatus';
import { fetchTimezoneColumnPropertyDefinition } from '../api/properties';
import { CreationSourceOptions, TASK_REPEAT_STATUS, TASK_TEMPLATE } from '../constants/viewFilters';
import { useSelector } from '../context/Redux';
import { isUngatedForLegacyBoardSelector } from '../redux/selectors/auth';
import { GROUP_NAMES, reportNewTasksError } from '../tracking/errors';
import { updateTimezonePropertyDefinition } from '../utils/properties';
import { getSuperstoreCachedValue, getSuperstoreKey, setSuperstoreCachedValue } from '../utils/superstore';
import { useLinkedInIntegrationStatus } from './auth';
const timezonePropertyDefinitionSuperstoreKey = 'timezonePropertyDefinition';

// In order to populate XOFilterEditor with our custom list of source options
const getTaskSourceOptions = memoize(() => {
  return List([PropertyOptionRecord({
    // https://hubspot.slack.com/archives/CPMTBGSAG/p1646239307030469
    // As of fall 2021, tasks created from workflows should all have AUTOMATION_PLATFORM for this value.
    // The WORKFLOWS value was used previously but should no longer be getting set by workflows
    value: CreationSourceOptions.AUTOMATION_PLATFORM,
    label: I18n.text('tasks-table.filters.creationSource.workflow')
  }), PropertyOptionRecord({
    value: CreationSourceOptions.SEQUENCE,
    label: I18n.text('tasks-table.filters.creationSource.sequence')
  }), PropertyOptionRecord({
    value: CreationSourceOptions.SALESFORCE,
    label: I18n.text('tasks-table.filters.creationSource.salesforce')
  })]);
});
function _transformPropertyRecord(propertyRecord, {
  hasLinkedInSalesNavigatorAccess,
  hasLegacyBoardAccess
}) {
  if (propertyRecord.name === ENGAGEMENT_SOURCE) {
    return propertyRecord.set('type', ENUMERATION).set('options', List(getTaskSourceOptions()));
  }
  if (propertyRecord.name === ENGAGEMENT_SOURCE_ID) {
    return propertyRecord.set('type', ENUMERATION);
  }
  let filteredOptions = get('options', propertyRecord);
  if (filteredOptions) {
    filteredOptions = filterNot(option => option.hidden, filteredOptions);
  }
  if (propertyRecord.name === TYPE) {
    filteredOptions = filterNot(option => !hasLinkedInSalesNavigatorAccess && [LINKED_IN_MESSAGE, LINKED_IN_CONNECT].includes(option.value), filteredOptions);
  }
  if (propertyRecord.name === STATUS) {
    filteredOptions = filterNot(option => !hasLegacyBoardAccess && [DEFERRED, IN_PROGRESS, WAITING].includes(option.value), filteredOptions);
  }
  return propertyRecord.set('options', filteredOptions);
}
function formatPropertiesFromGraphQL(propertyDefinitionsArray, permissions = {
  hasLinkedInSalesNavigatorAccess: false,
  hasLegacyBoardAccess: false
}) {
  return propertyDefinitionsArray.reduce((propertyMap, propertyObject) => {
    return Object.assign({}, propertyMap, {
      [propertyObject.name]: _transformPropertyRecord(PropertyRecord.fromJS(propertyObject), permissions)
    });
  }, {});
}
const taskPropertiesQuery = ("__gql__", "{\"kind\":\"Document\",\"definitions\":[{\"kind\":\"OperationDefinition\",\"operation\":\"query\",\"name\":{\"kind\":\"Name\",\"value\":\"TasksUIObjectProperties\"},\"variableDefinitions\":[{\"kind\":\"VariableDefinition\",\"variable\":{\"kind\":\"Variable\",\"name\":{\"kind\":\"Name\",\"value\":\"objectType\"}},\"type\":{\"kind\":\"NonNullType\",\"type\":{\"kind\":\"NamedType\",\"name\":{\"kind\":\"Name\",\"value\":\"String\"}}}},{\"kind\":\"VariableDefinition\",\"variable\":{\"kind\":\"Variable\",\"name\":{\"kind\":\"Name\",\"value\":\"propertyNames\"}},\"type\":{\"kind\":\"NonNullType\",\"type\":{\"kind\":\"ListType\",\"type\":{\"kind\":\"NonNullType\",\"type\":{\"kind\":\"NamedType\",\"name\":{\"kind\":\"Name\",\"value\":\"String\"}}}}}}],\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"objectTypeDefinition\"},\"arguments\":[{\"kind\":\"Argument\",\"name\":{\"kind\":\"Name\",\"value\":\"objectType\"},\"value\":{\"kind\":\"Variable\",\"name\":{\"kind\":\"Name\",\"value\":\"objectType\"}}}],\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"id\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"propertyDefinitions\"},\"arguments\":[{\"kind\":\"Argument\",\"name\":{\"kind\":\"Name\",\"value\":\"names\"},\"value\":{\"kind\":\"Variable\",\"name\":{\"kind\":\"Name\",\"value\":\"propertyNames\"}}}],\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"objectTypeId\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"description\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"displayMode\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"displayOrder\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"externalOptions\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"externalOptionsReferenceType\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"fieldType\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"formField\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hidden\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hubspotDefined\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"label\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"name\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"options\"},\"selectionSet\":{\"kind\":\"SelectionSet\",\"selections\":[{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"label\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"value\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"hidden\"}}]}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"readOnlyValue\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"referencedObjectType\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"textDisplayHint\"}},{\"kind\":\"Field\",\"name\":{\"kind\":\"Name\",\"value\":\"type\"}}]}}]}}]}}]}", {
  id: null,
  kind: "Document",
  definitions: [{
    kind: "OperationDefinition",
    operation: "query",
    name: {
      kind: "Name",
      value: "TasksUIObjectProperties"
    },
    variableDefinitions: [{
      kind: "VariableDefinition",
      variable: {
        kind: "Variable",
        name: {
          kind: "Name",
          value: "objectType"
        }
      },
      type: {
        kind: "NonNullType",
        type: {
          kind: "NamedType",
          name: {
            kind: "Name",
            value: "String"
          }
        }
      }
    }, {
      kind: "VariableDefinition",
      variable: {
        kind: "Variable",
        name: {
          kind: "Name",
          value: "propertyNames"
        }
      },
      type: {
        kind: "NonNullType",
        type: {
          kind: "ListType",
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: {
                kind: "Name",
                value: "String"
              }
            }
          }
        }
      }
    }],
    selectionSet: {
      kind: "SelectionSet",
      selections: [{
        kind: "Field",
        name: {
          kind: "Name",
          value: "objectTypeDefinition"
        },
        arguments: [{
          kind: "Argument",
          name: {
            kind: "Name",
            value: "objectType"
          },
          value: {
            kind: "Variable",
            name: {
              kind: "Name",
              value: "objectType"
            }
          }
        }],
        selectionSet: {
          kind: "SelectionSet",
          selections: [{
            kind: "Field",
            name: {
              kind: "Name",
              value: "id"
            }
          }, {
            kind: "Field",
            name: {
              kind: "Name",
              value: "propertyDefinitions"
            },
            arguments: [{
              kind: "Argument",
              name: {
                kind: "Name",
                value: "names"
              },
              value: {
                kind: "Variable",
                name: {
                  kind: "Name",
                  value: "propertyNames"
                }
              }
            }],
            selectionSet: {
              kind: "SelectionSet",
              selections: [{
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "objectTypeId"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "description"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "displayMode"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "displayOrder"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "externalOptions"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "externalOptionsReferenceType"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "fieldType"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "formField"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "hidden"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "hubspotDefined"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "label"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "name"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "options"
                },
                selectionSet: {
                  kind: "SelectionSet",
                  selections: [{
                    kind: "Field",
                    name: {
                      kind: "Name",
                      value: "label"
                    }
                  }, {
                    kind: "Field",
                    name: {
                      kind: "Name",
                      value: "value"
                    }
                  }, {
                    kind: "Field",
                    name: {
                      kind: "Name",
                      value: "hidden"
                    }
                  }]
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "readOnlyValue"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "referencedObjectType"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "textDisplayHint"
                }
              }, {
                kind: "Field",
                name: {
                  kind: "Name",
                  value: "type"
                }
              }]
            }
          }]
        }
      }]
    }
  }]
});
export const useObjectProperties = (objectType, propertyNames) => {
  const {
    loading,
    error,
    data
  } = useQuery(taskPropertiesQuery, {
    fetchPolicy: 'cache-first',
    variables: {
      objectType,
      propertyNames
    }
  });
  const hasLinkedInSalesNavigatorAccess = useLinkedInIntegrationStatus();
  const hasLegacyBoardAccess = useSelector(isUngatedForLegacyBoardSelector);
  const properties = useMemo(() => {
    const propertyDefinitionsArray = getIn(['objectTypeDefinition', 'propertyDefinitions'], data);
    if (!propertyDefinitionsArray) {
      return {};
    }
    return formatPropertiesFromGraphQL(propertyDefinitionsArray, {
      hasLinkedInSalesNavigatorAccess,
      hasLegacyBoardAccess
    });
  }, [data, hasLinkedInSalesNavigatorAccess, hasLegacyBoardAccess]);
  return {
    loading,
    error,
    properties
  };
};
export const useTaskProperties = partial(useObjectProperties, TASK);
export const useTaskTemplateProperties = partial(useObjectProperties, TASK_TEMPLATE);
export const useCompanyProperties = partial(useObjectProperties, COMPANY);
export const useContactTimezoneProperty = () => {
  const [fetchPropertyState, setFetchPropertyState] = useState({
    error: null,
    loading: true,
    timezonePropertyDefinition: null
  });
  useEffect(() => {
    getSuperstoreCachedValue(getSuperstoreKey(timezonePropertyDefinitionSuperstoreKey)).then(data => {
      const options = getIn(['options'], data);
      // Verify property definition exists and has options loaded
      if (data && options && options.length > 0) {
        setFetchPropertyState({
          error: null,
          loading: false,
          timezonePropertyDefinition: PropertyRecord.fromJS(data)
        });
      } else {
        // If cache comes back and does not have the necessary data, treat as error in 'get' and have it be caught higher up
        throw new Error('No cache data found');
      }
    })
    // Handle error for getting data from superstore cache, including errors with getSuperstoreCachedValue call itself or if required data is not in cache
    .catch(() => {
      fetchTimezoneColumnPropertyDefinition().then(timezonePropertyDefinitionResponse => {
        if (timezonePropertyDefinitionResponse) {
          // Convert to JS before storing in cache
          setSuperstoreCachedValue(getSuperstoreKey(timezonePropertyDefinitionSuperstoreKey), timezonePropertyDefinitionResponse).catch(() => {
            // ignore error with set
            console.error(`Error writing property definition to cache`);
          });
          setFetchPropertyState({
            error: null,
            loading: false,
            timezonePropertyDefinition: timezonePropertyDefinitionResponse
          });
        } else {
          // In case where timezone data comes back empty, treat as if there was error while fetching
          throw new Error('Error while processing timezone property definition');
        }
      }).catch(timezoneFetchError => {
        // Handle error with fetchTimezoneColumnPropertyDefinition call
        console.error('Failed to fetch timezone property definition');
        reportNewTasksError(timezoneFetchError, {
          groupName: GROUP_NAMES.PROPERTIES,
          functionName: 'getTimezonePropertyDefinition'
        });
        setFetchPropertyState({
          error: timezoneFetchError,
          loading: false,
          timezonePropertyDefinition: null
        });
      });
    });
  }, []);
  return fetchPropertyState;
};
const _mergeRepeatStatusPropertyDefinitions = (taskRepeatStatusProperty, taskTemplateRepeatStatusProperty) => {
  if (!taskRepeatStatusProperty || !taskTemplateRepeatStatusProperty) {
    return taskRepeatStatusProperty;
  }
  const taskTemplateOptions = get('options', taskTemplateRepeatStatusProperty);
  return taskRepeatStatusProperty.set('options', taskTemplateOptions);
};
export const useMergeTaskProperties = propertyNames => {
  const {
    loading: taskPropertiesLoading,
    error: taskError,
    properties: taskProperties
  } = useTaskProperties(propertyNames);
  const {
    loading: taskTemplatePropertiesLoading,
    error: taskTemplateError,
    properties: taskTemplateProperties
  } = useTaskTemplateProperties([TASK_REPEAT_STATUS]);
  const {
    [TASK_REPEAT_STATUS]: repeatProperty,
    rest
  } = taskTemplateProperties;
  const repeatStatusProperty = useMemo(() => _mergeRepeatStatusPropertyDefinitions(taskProperties[REPEAT_STATUS], repeatProperty), [taskProperties, repeatProperty]);
  const loading = taskPropertiesLoading || taskTemplatePropertiesLoading;
  const error = taskError || taskTemplateError;
  const noProperties = isEmpty(taskProperties) || isEmpty(taskTemplateProperties);
  return useMemo(() => {
    if (loading || error || noProperties) {
      return {
        loading,
        error
      };
    }
    const properties = Object.assign({}, taskProperties, rest, {
      [InboundDbImportProperty.name]: InboundDbImportProperty
    });
    if (repeatStatusProperty) {
      properties[REPEAT_STATUS] = repeatStatusProperty;
    }
    return {
      loading,
      error,
      properties
    };
  }, [loading, error, noProperties, taskProperties, rest, repeatStatusProperty]);
};
export const usePropertiesWithTimezoneDefinition = propertyNames => {
  const {
    loading: propertiesLoading,
    error,
    properties
  } = useMergeTaskProperties(propertyNames);
  const {
    loading: timezonePropertyLoading,
    timezonePropertyDefinition
  } = useContactTimezoneProperty();
  return useMemo(() => {
    const loading = propertiesLoading || timezonePropertyLoading;
    if (error || !properties || loading) {
      return {
        error,
        loading,
        properties: {}
      };
    }
    if (!timezonePropertyDefinition) {
      return {
        error,
        loading,
        properties
      };
    }
    return {
      error,
      loading,
      properties: updateTimezonePropertyDefinition(Object.assign({}, properties, {
        [timezonePropertyDefinition.name]: timezonePropertyDefinition
      }))
    };
  }, [error, properties, propertiesLoading, timezonePropertyDefinition, timezonePropertyLoading]);
};